import { useState, useEffect, useRef, type ComponentType, type ReactNode } from 'react';
import {
  Save,
  Building2,
  Shield,
  Bell,
  Cpu,
  KeyRound,
  FlaskConical,
  Loader2,
  Sparkles,
  FileText,
  Plus,
  X,
  Trash2,
  ClipboardCopy,
  Check,
  BookOpen,
  MessageSquare,
  Bot,
  Search,
  Upload,
  Mic,
  Share2,
  Bug,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { LabelWithHint } from '@/components/help/LabelWithHint';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import type { AppSettings, ProviderCredential, ResearchFocusArea } from '@/types';
import { api } from '@/lib/api';
import { SETTINGS_TAB_SESSION_KEY } from '@/lib/settings-deep-link';
import { useBrandingStore } from '@/stores/branding';
import { ModelRouterModelSelect } from '@/components/admin/ModelRouterModelSelect';
import { BugReportTargetPanel } from '@/components/admin/BugReportTargetPanel';
import {
  fetchAdminModelRouterModels,
  filterRouterModels,
  MODELROUTER_DEFAULT_BASE_URL,
  type ModelRouterModelInfo,
} from '@/lib/modelrouter-models';

const DEMO_SETTINGS: AppSettings = {
  app_name: 'Leftclick AG',
  app_logo_url: '',
  app_logo_url_dark: '',
  pdf_activity_report_title: 'Marketing-Aktivitaet Detailreport',
  pdf_show_logo: true,
  pdf_include_checklists_default: true,
  pdf_include_comments_default: true,
  pdf_goal_fallback_text: '',
  registration_enabled: false,
  invite_required: true,
  default_role: 'editor',
  mfa_enforced: false,
  session_timeout_minutes: 60,
  notification_email_enabled: true,
  notification_email_from: 'noreply@leftclick.ch',
  ai_default_provider: 'openai',
  ai_default_model: 'gpt-4o',
  ai_default_credential_id: '',
  assistant_enabled: true,
  assistant_autonomy_mode: 'mostly_auto_execute',
  assistant_max_risk_score: 70,
  assistant_deep_research_enabled: true,
  assistant_research_default_focus_area: 'market',
  assistant_research_default_provider: 'openai',
  assistant_research_default_model: 'gpt-5.4-pro',
  assistant_research_custom_endpoint: '',
  assistant_research_max_iterations: 4,
  assistant_budget_guardrail_enabled: true,
  assistant_budget_auto_execute_limit: 2500,
  social_default_timezone: 'Europe/Zurich',
  social_quiet_hours: '{"from":"22:00","to":"07:00"}',
  social_require_approval: false,
  social_story_max_hours_ahead: 24,
  social_utm_defaults: '{"utm_source":"leftclick","utm_medium":"social"}',
  social_default_link_tracking: true,
  social_notify_on_fail: true,
};

const PROVIDER_EXAMPLES: Record<string, { name: string; config: Record<string, unknown>; credentials: Record<string, unknown> }> = {
  openai: {
    name: 'OpenAI Production',
    config: { model: 'gpt-4o' },
    credentials: { apiKey: 'REPLACE_WITH_OPENAI_KEY' },
  },
  azure_openai: {
    name: 'Azure OpenAI Production',
    config: {
      endpoint: 'https://leftclick-ai.services.ai.azure.com',
      deployment: 'gpt-4o',
      model: 'gpt-4o',
      whisper_deployment: 'gpt-4o-mini-transcribe',
    },
    credentials: { apiKey: 'REPLACE_WITH_AZURE_KEY' },
  },
  azure_cognitive_services: {
    name: 'Azure Cognitive Services (Deep Research)',
    config: {
      endpoint: 'https://your-resource.cognitiveservices.azure.com',
      deployment: 'gpt-5.4-pro',
      model: 'gpt-5.4-pro',
      apiVersion: 'preview',
    },
    credentials: { apiKey: 'REPLACE_WITH_AZURE_KEY' },
  },
  azure_foundry: {
    name: 'Azure AI Foundry (Legacy)',
    config: {
      projectEndpoint: 'https://leftclick-ai.services.ai.azure.com',
      deepResearchDeployment: 'o3-deep-research',
      chatModel: 'gpt-4o',
      bingConnectionName: 'grounding-bing-search',
    },
    credentials: { apiKey: 'REPLACE_WITH_AZURE_KEY' },
  },
  custom_o3gpt: {
    name: 'Custom O3GPT Research',
    config: {
      endpoint: 'https://your-o3gpt-endpoint.example.com/v1/research',
      model: 'o3',
    },
    credentials: { apiKey: 'REPLACE_WITH_O3GPT_KEY' },
  },
  modelrouter: {
    name: 'Leftclick Model Router',
    config: {
      baseURL: 'https://router.leftclick.ch/v1',
      model: 'auto',
      transcription_model: '',
    },
    credentials: { apiKey: 'REPLACE_WITH_mr_live_KEY' },
  },
  google_ads: {
    name: 'Google Ads Production',
    config: { customerId: '123-456-7890' },
    credentials: {
      clientId: 'REPLACE_WITH_CLIENT_ID',
      clientSecret: 'REPLACE_WITH_CLIENT_SECRET',
      refreshToken: 'REPLACE_WITH_REFRESH_TOKEN',
      developerToken: 'REPLACE_WITH_DEVELOPER_TOKEN',
    },
  },
  linkedin_ads: {
    name: 'LinkedIn Ads Production',
    config: { accountId: 'REPLACE_WITH_ACCOUNT_ID' },
    credentials: { accessToken: 'REPLACE_WITH_LINKEDIN_ACCESS_TOKEN' },
  },
  mailchimp: {
    name: 'Mailchimp Production',
    config: { audienceId: 'REPLACE_WITH_AUDIENCE_ID' },
    credentials: { apiKey: 'REPLACE_WITH_MAILCHIMP_KEY', serverPrefix: 'us1' },
  },
  lemlist: {
    name: 'Lemlist Production',
    config: {},
    credentials: { apiKey: 'REPLACE_WITH_LEMLIST_KEY' },
  },
};

const AI_CONNECT_GUIDE = [
  {
    title: '1) Model Router oder Azure als Credential anlegen',
    text: 'Unter «Provider Credentials»: Typ «Model Router» (Gateway-URL + mr_-Key) oder «Azure OpenAI». Alles wird verschlüsselt in der Datenbank gespeichert — keine .env auf dem Server nötig.',
  },
  {
    title: '2) Im KI-Bereich Profil wählen',
    text: 'Standard-Provider und «Provider-Profil» auf das gespeicherte Credential setzen. Optional Standard-Modell (z. B. auto oder gpt-4o).',
  },
  {
    title: '3) Verbindung testen und speichern',
    text: 'Credential mit «Testen» prüfen (auch im Dialog vor dem Speichern). Im KI-Bereich «Verbindung testen», dann «KI-Einstellungen speichern».',
  },
];

const PROVIDER_INPUT_HINTS: Record<string, { config: string; credentials: string }> = {
  openai: {
    config: '{ "model": "gpt-4o" }',
    credentials: '{ "apiKey": "sk-..." }',
  },
  azure_openai: {
    config:
      '{ "endpoint": "https://<resource>.openai.azure.com", "deployment": "gpt-4o", "model": "gpt-4o", "whisper_deployment": "gpt-4o-mini-transcribe", "whisper_api_version": "2025-03-01-preview" }',
    credentials: '{ "apiKey": "<azure-key>" }',
  },
  azure_cognitive_services: {
    config: '{ "endpoint": "https://<resource>.cognitiveservices.azure.com", "deployment": "gpt-5.4-pro", "model": "gpt-5.4-pro", "apiVersion": "preview" }',
    credentials: '{ "apiKey": "<azure-key>" }',
  },
  custom_o3gpt: {
    config: '{ "endpoint": "https://your-endpoint/v1/research", "model": "o3" }',
    credentials: '{ "apiKey": "..." }',
  },
  modelrouter: {
    config:
      '{ "baseURL": "https://router.leftclick.ch/v1", "model": "auto", "transcription_model": "" }',
    credentials: '{ "apiKey": "mr_live_..." }',
  },
  ollama: {
    config: '{ "baseURL": "http://localhost:11434/v1", "model": "llama3.1" }',
    credentials: '{ }',
  },
  google_ads: {
    config: '{ "customerId": "123-456-7890" }',
    credentials: '{ "clientId": "...", "clientSecret": "...", "refreshToken": "...", "developerToken": "..." }',
  },
  linkedin_ads: {
    config: '{ "accountId": "..." }',
    credentials: '{ "accessToken": "..." }',
  },
  mailchimp: {
    config: '{ "audienceId": "..." }',
    credentials: '{ "apiKey": "...", "serverPrefix": "us1" }',
  },
  lemlist: {
    config: '{ }',
    credentials: '{ "apiKey": "..." }',
  },
};

interface LoginAmbiencePalette {
  bg1: string;
  bg2: string;
  bg3: string;
}

interface LoginAmbienceConfig {
  is_enabled: boolean;
  headlines: string[];
  sublines: string[];
  highlights: string[];
  palettes: LoginAmbiencePalette[];
}

const DEFAULT_LOGIN_AMBIENCE_CONFIG: LoginAmbienceConfig = {
  is_enabled: true,
  headlines: [
    'Launch smarter campaigns, faster.',
    'Von Idee zu Performance in einem Flow.',
    'Marketing-Execution ohne Tool-Chaos.',
  ],
  sublines: [
    'Planung, Freigaben und KPIs zentral an einem Ort.',
    'KI-Assistenz trifft auf saubere Team-Prozesse.',
    'Alles bereit fuer den naechsten starken Kampagnen-Launch.',
  ],
  highlights: [
    'Live-Dashboards fuer Pipeline und Budget',
    'KI-Workflows fuer Content und Briefings',
    'Saubere Freigaben mit Rollen und Rechten',
  ],
  palettes: [
    { bg1: 'rgba(37, 99, 235, 0.20)', bg2: 'rgba(56, 189, 248, 0.16)', bg3: 'rgba(99, 102, 241, 0.18)' },
    { bg1: 'rgba(168, 85, 247, 0.20)', bg2: 'rgba(59, 130, 246, 0.16)', bg3: 'rgba(14, 165, 233, 0.18)' },
  ],
};

function parseMultilineInput(input: string): string[] {
  return input
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

function ambienceToText(config: LoginAmbienceConfig) {
  return {
    headlines: config.headlines.join('\n'),
    sublines: config.sublines.join('\n'),
    highlights: config.highlights.join('\n'),
    palettes: JSON.stringify(config.palettes, null, 2),
  };
}

function normalizeSettingsPayload(input: unknown): AppSettings {
  if (!input || typeof input !== 'object') return DEMO_SETTINGS;
  const map = input as Record<string, unknown>;
  const flattened: Record<string, unknown> = {};
  Object.entries(map).forEach(([k, v]) => {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      Object.assign(flattened, v as Record<string, unknown>);
    } else {
      flattened[k] = v;
    }
  });
  return {
    ...DEMO_SETTINGS,
    ...flattened,
    registration_enabled: String(flattened.registration_enabled ?? DEMO_SETTINGS.registration_enabled) === 'true' || flattened.registration_enabled === true,
    invite_required: String(flattened.invite_required ?? DEMO_SETTINGS.invite_required) === 'true' || flattened.invite_required === true,
    mfa_enforced: String(flattened.mfa_enforced ?? DEMO_SETTINGS.mfa_enforced) === 'true' || flattened.mfa_enforced === true,
    notification_email_enabled:
      String(flattened.notification_email_enabled ?? DEMO_SETTINGS.notification_email_enabled) === 'true' || flattened.notification_email_enabled === true,
    pdf_show_logo:
      String(flattened.pdf_show_logo ?? DEMO_SETTINGS.pdf_show_logo) === 'true' || flattened.pdf_show_logo === true,
    pdf_include_checklists_default:
      String(flattened.pdf_include_checklists_default ?? DEMO_SETTINGS.pdf_include_checklists_default) === 'true' ||
      flattened.pdf_include_checklists_default === true,
    pdf_include_comments_default:
      String(flattened.pdf_include_comments_default ?? DEMO_SETTINGS.pdf_include_comments_default) === 'true' ||
      flattened.pdf_include_comments_default === true,
    assistant_enabled: String(flattened.assistant_enabled ?? true) === 'true' || flattened.assistant_enabled === true,
    assistant_deep_research_enabled:
      String(flattened.assistant_deep_research_enabled ?? true) === 'true' || flattened.assistant_deep_research_enabled === true,
    assistant_budget_guardrail_enabled:
      String(flattened.assistant_budget_guardrail_enabled ?? true) === 'true' || flattened.assistant_budget_guardrail_enabled === true,
    session_timeout_minutes: Number(flattened.session_timeout_minutes ?? DEMO_SETTINGS.session_timeout_minutes),
    assistant_max_risk_score: Number(flattened.assistant_max_risk_score ?? DEMO_SETTINGS.assistant_max_risk_score),
    assistant_research_default_focus_area: (() => {
      const raw = String(flattened.assistant_research_default_focus_area ?? DEMO_SETTINGS.assistant_research_default_focus_area ?? 'market');
      const allowed: ResearchFocusArea[] = ['market', 'competitors', 'audience', 'channels', 'trends'];
      return allowed.includes(raw as ResearchFocusArea) ? (raw as ResearchFocusArea) : 'market';
    })(),
    assistant_research_max_iterations: Number(flattened.assistant_research_max_iterations ?? DEMO_SETTINGS.assistant_research_max_iterations),
    assistant_budget_auto_execute_limit: Number(flattened.assistant_budget_auto_execute_limit ?? DEMO_SETTINGS.assistant_budget_auto_execute_limit),
    social_require_approval:
      String(flattened.social_require_approval ?? DEMO_SETTINGS.social_require_approval) === 'true' ||
      flattened.social_require_approval === true,
    social_default_link_tracking:
      String(flattened.social_default_link_tracking ?? DEMO_SETTINGS.social_default_link_tracking) === 'true' ||
      flattened.social_default_link_tracking === true,
    social_notify_on_fail:
      String(flattened.social_notify_on_fail ?? DEMO_SETTINGS.social_notify_on_fail) === 'true' ||
      flattened.social_notify_on_fail === true,
    social_story_max_hours_ahead: Number(
      flattened.social_story_max_hours_ahead ?? DEMO_SETTINGS.social_story_max_hours_ahead
    ),
    social_quiet_hours:
      typeof flattened.social_quiet_hours === 'string'
        ? flattened.social_quiet_hours
        : flattened.social_quiet_hours && typeof flattened.social_quiet_hours === 'object'
          ? JSON.stringify(flattened.social_quiet_hours)
          : DEMO_SETTINGS.social_quiet_hours,
    social_utm_defaults:
      typeof flattened.social_utm_defaults === 'string'
        ? flattened.social_utm_defaults
        : flattened.social_utm_defaults && typeof flattened.social_utm_defaults === 'object'
          ? JSON.stringify(flattened.social_utm_defaults)
          : DEMO_SETTINGS.social_utm_defaults,
  };
}

function parseSocialQuietHours(raw: string | undefined): { from: string; to: string } {
  try {
    const parsed = JSON.parse(raw || '{}') as { from?: unknown; to?: unknown };
    return {
      from: typeof parsed.from === 'string' && parsed.from ? parsed.from : '22:00',
      to: typeof parsed.to === 'string' && parsed.to ? parsed.to : '07:00',
    };
  } catch {
    return { from: '22:00', to: '07:00' };
  }
}

function parseSocialUtmDefaults(raw: string | undefined): { utm_source: string; utm_medium: string } {
  try {
    const parsed = JSON.parse(raw || '{}') as { utm_source?: unknown; utm_medium?: unknown };
    return {
      utm_source: typeof parsed.utm_source === 'string' ? parsed.utm_source : 'leftclick',
      utm_medium: typeof parsed.utm_medium === 'string' ? parsed.utm_medium : 'social',
    };
  } catch {
    return { utm_source: 'leftclick', utm_medium: 'social' };
  }
}

function HintBlock({
  value,
  copied,
  onCopy,
  onUse,
}: {
  value: string;
  copied: boolean;
  onCopy: () => void;
  onUse: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-xs text-muted hover:text-foreground dark:hover:text-foreground"
      >
        {open ? '▾ Beispiel ausblenden' : '▸ Beispiel anzeigen'}
      </button>
      {open && (
        <div className="mt-2 overflow-hidden rounded-lg border border-border bg-background dark:bg-card-hover/50">
          <div className="flex items-center justify-between border-b border-border px-3 py-1.5">
            <span className="text-[11px] font-medium text-muted">Vorlage</span>
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={onUse}
                className="flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-medium text-foreground hover:bg-card-hover dark:text-muted dark:hover:bg-primary-hover/30"
              >
                Übernehmen
              </button>
              <button
                type="button"
                onClick={onCopy}
                className="flex items-center gap-1 rounded px-2 py-0.5 text-[11px] text-muted hover:bg-card-hover"
              >
                {copied ? (
                  <><Check className="h-3 w-3 text-success" />Kopiert</>
                ) : (
                  <><ClipboardCopy className="h-3 w-3" />Kopieren</>
                )}
              </button>
            </div>
          </div>
          <pre className="overflow-x-auto whitespace-pre-wrap break-all p-3 text-[11px] leading-relaxed text-muted">
            {value}
          </pre>
        </div>
      )}
    </div>
  );
}

function AiSettingsSection({
  title,
  description,
  icon: Icon,
  children,
}: {
  title: string;
  description?: string;
  icon: ComponentType<{ className?: string }>;
  children: ReactNode;
}) {
  return (
    <section className="rounded-xl border border-border bg-background/80 p-4 dark:bg-card-hover/30">
      <div className="mb-4 flex items-start gap-3 border-b border-border/80 pb-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-card-hover text-foreground dark:bg-primary-hover/60 dark:text-muted">
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          {description ? (
            <p className="mt-0.5 text-xs text-muted">{description}</p>
          ) : null}
        </div>
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

type SectionId = 'general' | 'pdf' | 'ambience' | 'auth' | 'notifications' | 'social' | 'ai' | 'providers' | 'bugs';

const TABS: { id: SectionId; label: string; icon: ReactNode }[] = [
  { id: 'general', label: 'Allgemein', icon: <Building2 className="h-4 w-4" /> },
  { id: 'pdf', label: 'PDF Export', icon: <FileText className="h-4 w-4" /> },
  { id: 'ambience', label: 'Login Ambience', icon: <Sparkles className="h-4 w-4" /> },
  { id: 'auth', label: 'Authentifizierung', icon: <Shield className="h-4 w-4" /> },
  { id: 'notifications', label: 'Benachrichtigungen', icon: <Bell className="h-4 w-4" /> },
  { id: 'social', label: 'Social', icon: <Share2 className="h-4 w-4" /> },
  { id: 'ai', label: 'KI / AI', icon: <Cpu className="h-4 w-4" /> },
  { id: 'providers', label: 'Provider Credentials', icon: <KeyRound className="h-4 w-4" /> },
  { id: 'bugs', label: 'Bugmeldungen', icon: <Bug className="h-4 w-4" /> },
];

export function SettingsAdminPage() {
  const [activeTab, setActiveTab] = useState<SectionId>('general');
  const [settings, setSettings] = useState<AppSettings>(DEMO_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [settingsAttempt, setSettingsAttempt] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [roles, setRoles] = useState<{ id: string; name: string }[]>([]);
  const [credentials, setCredentials] = useState<ProviderCredential[]>([]);
  const [credentialLoading, setCredentialLoading] = useState(true);
  const [testingCredentialId, setTestingCredentialId] = useState<string | null>(null);
  const [deletingCredentialId, setDeletingCredentialId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [copiedHint, setCopiedHint] = useState<'config' | 'credentials' | null>(null);
  const [testFeedback, setTestFeedback] = useState<Record<string, { ok: boolean; message: string }>>({});
  const [testingAiConfig, setTestingAiConfig] = useState(false);
  const [aiConfigTestFeedback, setAiConfigTestFeedback] = useState<{ ok: boolean; message: string } | null>(null);
  const [testingCredentialDraft, setTestingCredentialDraft] = useState(false);
  const [credentialDraftTestFeedback, setCredentialDraftTestFeedback] = useState<{ ok: boolean; message: string } | null>(null);
  const [routerModels, setRouterModels] = useState<ModelRouterModelInfo[]>([]);
  const [loadingRouterModels, setLoadingRouterModels] = useState(false);
  const [draftRouterModels, setDraftRouterModels] = useState<ModelRouterModelInfo[]>([]);
  const [loadingDraftRouterModels, setLoadingDraftRouterModels] = useState(false);
  const [editingCredentialId, setEditingCredentialId] = useState<string | null>(null);
  const [credentialModalOpen, setCredentialModalOpen] = useState(false);
  const [newCredential, setNewCredential] = useState({
    type: 'openai',
    name: '',
    config: '{}',
    credentials: '{}',
  });
  const [ambienceConfig, setAmbienceConfig] = useState<LoginAmbienceConfig>(DEFAULT_LOGIN_AMBIENCE_CONFIG);
  const [ambienceDraft, setAmbienceDraft] = useState(() => ambienceToText(DEFAULT_LOGIN_AMBIENCE_CONFIG));
  const logoLightInputRef = useRef<HTMLInputElement>(null);
  const logoDarkInputRef = useRef<HTMLInputElement>(null);
  const [logoUploading, setLogoUploading] = useState<'light' | 'dark' | null>(null);
  const [logoDragVariant, setLogoDragVariant] = useState<'light' | 'dark' | null>(null);

  const hasActiveOpenAiCredential = credentials.some((c) => c.type === 'openai' && c.status === 'active');
  const hasAzureWhisperDeploymentInConfig = credentials.some(
    (c) =>
      (c.type === 'azure_openai' || c.type === 'azure_cognitive_services') &&
      c.status === 'active' &&
      (Boolean(
        (typeof c.config?.whisper_deployment === 'string' && c.config.whisper_deployment.trim()) ||
          (typeof c.config?.whisperDeployment === 'string' && c.config.whisperDeployment.trim()) ||
          (typeof c.config?.audio_transcription_deployment === 'string' && c.config.audio_transcription_deployment.trim())
      ))
  );
  const hasActiveModelRouterCredential = credentials.some((c) => c.type === 'modelrouter' && c.status === 'active');
  const voiceServerLikelyConfigured =
    hasActiveOpenAiCredential || hasAzureWhisperDeploymentInConfig || hasActiveModelRouterCredential;

  useEffect(() => {
    try {
      const tab = sessionStorage.getItem(SETTINGS_TAB_SESSION_KEY);
      if (tab === 'providers' || tab === 'ai' || tab === 'social') {
        setActiveTab(tab);
        sessionStorage.removeItem(SETTINGS_TAB_SESSION_KEY);
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setSettingsLoaded(false);
    setError(null);
    api
      .get<{ success?: boolean; data?: unknown; error?: string }>('/admin/settings')
      .then((res) => {
        if (res.success === false || !res.data || typeof res.data !== 'object' || Array.isArray(res.data)) {
          throw new Error(res.error || 'Einstellungen konnten nicht geladen werden.');
        }
        if (cancelled) return;
        setSettings(normalizeSettingsPayload(res.data));
        setSettingsLoaded(true);
      })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Einstellungen konnten nicht geladen werden.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    api.get<{ data?: { id: string; name: string }[] }>('/admin/roles').then((r: any) => setRoles((r as { data?: { id: string; name: string }[] }).data ?? [])).catch(() => {});
    api
      .get<{ data?: ProviderCredential[] }>('/admin/providers/credentials')
      .then((r: any) => setCredentials((r as { data?: ProviderCredential[] }).data ?? []))
      .catch(() => {})
      .finally(() => setCredentialLoading(false));
    api
      .get<{ data?: Partial<LoginAmbienceConfig> | null }>('/admin/login-ambience-config')
      .then((r: any) => {
        const data = (r as { data?: Partial<LoginAmbienceConfig> | null }).data;
        if (!data) return;
        const merged: LoginAmbienceConfig = {
          is_enabled: data.is_enabled ?? true,
          headlines: Array.isArray(data.headlines) && data.headlines.length ? data.headlines : DEFAULT_LOGIN_AMBIENCE_CONFIG.headlines,
          sublines: Array.isArray(data.sublines) && data.sublines.length ? data.sublines : DEFAULT_LOGIN_AMBIENCE_CONFIG.sublines,
          highlights: Array.isArray(data.highlights) && data.highlights.length ? data.highlights : DEFAULT_LOGIN_AMBIENCE_CONFIG.highlights,
          palettes: Array.isArray(data.palettes) && data.palettes.length ? data.palettes : DEFAULT_LOGIN_AMBIENCE_CONFIG.palettes,
        };
        setAmbienceConfig(merged);
        setAmbienceDraft(ambienceToText(merged));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [settingsAttempt]);

  const aiProviderCredentials = credentials.filter(
    (c) =>
      c.type === 'openai' ||
      c.type === 'azure_openai' ||
      c.type === 'azure_cognitive_services' ||
      c.type === 'modelrouter' ||
      c.type === 'ollama'
  );
  const mapCredentialTypeToProvider = (type: ProviderCredential['type']) => {
    if (type === 'azure_openai') return 'azure';
    if (type === 'ollama') return 'ollama';
    if (type === 'modelrouter') return 'modelrouter';
    return 'openai';
  };
  const providerLabel = (type: ProviderCredential['type']) => {
    if (type === 'azure_openai') return 'Azure OpenAI';
    if (type === 'azure_cognitive_services') return 'Azure Cognitive Services';
    if (type === 'azure_foundry') return 'Azure AI Foundry';
    if (type === 'custom_o3gpt') return 'Custom O3GPT';
    if (type === 'ollama') return 'Ollama';
    if (type === 'modelrouter') return 'Model Router';
    if (type === 'google_ads') return 'Google Ads';
    if (type === 'linkedin_ads') return 'LinkedIn Ads';
    if (type === 'mailchimp') return 'Mailchimp';
    if (type === 'lemlist') return 'Lemlist';
    return 'OpenAI';
  };
  const selectedProviderHint =
    PROVIDER_INPUT_HINTS[newCredential.type] ??
    PROVIDER_INPUT_HINTS.openai ??
    { config: '{}', credentials: '{}' };

  const selectedAiCredential = aiProviderCredentials.find((c) => c.id === settings.ai_default_credential_id);
  const isModelRouterAiActive =
    settings.ai_default_provider === 'modelrouter' || selectedAiCredential?.type === 'modelrouter';

  const loadRouterModelsForSettings = async () => {
    const credId = settings.ai_default_credential_id?.trim();
    if (!credId) {
      setAiConfigTestFeedback({
        ok: false,
        message: 'Bitte zuerst ein Model-Router-Profil unter «Provider-Profil» wählen.',
      });
      return;
    }
    setLoadingRouterModels(true);
    try {
      const models = await fetchAdminModelRouterModels({ credential_id: credId });
      setRouterModels(models);
    } catch (e) {
      setAiConfigTestFeedback({ ok: false, message: (e as Error).message || 'Modelle konnten nicht geladen werden' });
    } finally {
      setLoadingRouterModels(false);
    }
  };

  const loadRouterModelsForDraft = async () => {
    setLoadingDraftRouterModels(true);
    setCredentialDraftTestFeedback(null);
    try {
      if (editingCredentialId) {
        const credsRaw = newCredential.credentials.trim();
        if (!credsRaw || credsRaw === '{}') {
          const models = await fetchAdminModelRouterModels({ credential_id: editingCredentialId });
          setDraftRouterModels(models);
          return;
        }
      }
      const parsedConfig = JSON.parse(newCredential.config || '{}');
      const parsedCredentials = JSON.parse(newCredential.credentials || '{}');
      const models = await fetchAdminModelRouterModels({ config: parsedConfig, credentials: parsedCredentials });
      setDraftRouterModels(models);
    } catch (e) {
      setCredentialDraftTestFeedback({ ok: false, message: (e as Error).message || 'Modelle konnten nicht geladen werden' });
    } finally {
      setLoadingDraftRouterModels(false);
    }
  };

  const patchDraftConfigField = (key: string, value: string) => {
    try {
      const cfg = JSON.parse(newCredential.config || '{}') as Record<string, unknown>;
      cfg[key] = value;
      setNewCredential((s) => ({ ...s, config: JSON.stringify(cfg, null, 2) }));
    } catch {
      setNewCredential((s) => ({
        ...s,
        config: JSON.stringify({ [key]: value }, null, 2),
      }));
    }
  };

  const readDraftConfigField = (key: string, fallback = ''): string => {
    try {
      const cfg = JSON.parse(newCredential.config || '{}') as Record<string, unknown>;
      const v = cfg[key];
      return typeof v === 'string' ? v : fallback;
    } catch {
      return fallback;
    }
  };

  const update = (key: keyof AppSettings, value: string | number | boolean) => setSettings((s) => ({ ...s, [key]: value }));

  const uploadLogoFile = async (file: File | undefined, variant: 'light' | 'dark') => {
    if (!file) return;
    setError(null);
    setLogoUploading(variant);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('variant', variant);
      const res = await api.upload<{ success?: boolean; data?: { url?: string }; error?: string }>('/admin/settings/logo', formData);
      if (!res.success || !res.data?.url) {
        throw new Error(res.error || 'Logo-Upload fehlgeschlagen.');
      }
      if (variant === 'dark') {
        update('app_logo_url_dark', res.data.url);
      } else {
        update('app_logo_url', res.data.url);
      }
      await useBrandingStore.getState().load(true);
    } catch (e) {
      setError((e as Error).message ?? 'Logo-Upload fehlgeschlagen.');
    } finally {
      setLogoUploading(null);
    }
  };

  const saveSection = async (section: string) => {
    if (!settingsLoaded || saving) return;
    setSaving(section);
    setError(null);
    try {
      const generalKeys = ['registration_enabled', 'invite_required', 'default_role'];
      const authKeys = ['mfa_enforced', 'session_timeout_minutes'];
      const belongsToSection = (key: string) => {
        if (section === 'general') return key.startsWith('app_') || generalKeys.includes(key);
        if (section === 'auth') return authKeys.includes(key);
        if (section === 'ai') return key.startsWith('ai_') || key.startsWith('assistant_');
        return ['pdf', 'notification', 'social'].includes(section) && key.startsWith(`${section}_`);
      };
      const payload = Object.entries(settings).filter(([key]) => belongsToSection(key)).reduce<Record<string, string>>((acc, [key, value]) => {
        acc[key] = String(value ?? '');
        return acc;
      }, {});
      await api.patch('/admin/settings', payload);
      if (section === 'general') {
        await useBrandingStore.getState().load(true);
      }
    } catch (e) {
      setError((e as Error).message ?? 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(null);
    }
  };

  const saveCredential = async () => {
    setSaving('credentials');
    try {
      const parsedConfig = JSON.parse(newCredential.config || '{}');
      const parsedCredentials = JSON.parse(newCredential.credentials || '{}');
      if (newCredential.type === 'azure_openai') {
        const endpointValue =
          (typeof parsedConfig.endpoint === 'string' ? parsedConfig.endpoint : '') ||
          (typeof parsedConfig.baseURL === 'string' ? parsedConfig.baseURL : '');
        if (endpointValue) {
          let pathname = '';
          try {
            pathname = new URL(endpointValue).pathname.toLowerCase();
          } catch {
            pathname = endpointValue.toLowerCase();
          }
          if (pathname.includes('/models')) {
            setError(
              'Azure Endpoint ungültig: bitte Resource-Root verwenden (z.B. https://<resource>.services.ai.azure.com) und NICHT /models anhängen.'
            );
            return;
          }
        }
      }
      if (editingCredentialId) {
        const payload: Record<string, unknown> = {
          type: newCredential.type,
          name: newCredential.name,
          config: parsedConfig,
        };
        if (Object.keys(parsedCredentials).length > 0) {
          payload.credentials = parsedCredentials;
        }
        await api.patch(`/admin/providers/credentials/${editingCredentialId}`, payload);
      } else {
        await api.post('/admin/providers/credentials', {
          type: newCredential.type,
          name: newCredential.name,
          config: parsedConfig,
          credentials: parsedCredentials,
        });
      }
      const updated = await api.get<{ data?: ProviderCredential[] }>('/admin/providers/credentials');
      setCredentials(updated.data ?? []);
      setNewCredential({ type: 'openai', name: '', config: '{}', credentials: '{}' });
      setEditingCredentialId(null);
      setCredentialModalOpen(false);
    } catch (e) {
      setError((e as Error).message ?? 'Provider-Credential konnte nicht gespeichert werden.');
    } finally {
      setSaving(null);
    }
  };

  const saveAmbienceConfig = async () => {
    setSaving('ambience');
    setError(null);
    try {
      const parsedPalettes = JSON.parse(ambienceDraft.palettes || '[]') as LoginAmbiencePalette[];
      const payload: LoginAmbienceConfig = {
        is_enabled: ambienceConfig.is_enabled,
        headlines: parseMultilineInput(ambienceDraft.headlines),
        sublines: parseMultilineInput(ambienceDraft.sublines),
        highlights: parseMultilineInput(ambienceDraft.highlights),
        palettes: parsedPalettes,
      };
      await api.put('/admin/login-ambience-config', payload);
      setAmbienceConfig(payload);
      setAmbienceDraft(ambienceToText(payload));
    } catch (e) {
      setError((e as Error).message ?? 'Login-Ambience konnte nicht gespeichert werden.');
    } finally {
      setSaving(null);
    }
  };

  const applyProviderExample = () => {
    const tpl = PROVIDER_EXAMPLES[newCredential.type];
    if (!tpl) return;
    setNewCredential({
      type: newCredential.type,
      name: newCredential.name || tpl.name,
      config: JSON.stringify(tpl.config, null, 2),
      credentials: JSON.stringify(tpl.credentials, null, 2),
    });
  };

  const testCredential = async (id: string, chatModelOverride?: string) => {
    setTestingCredentialId(id);
    setError(null);
    try {
      const query = chatModelOverride?.trim() ? `?model=${encodeURIComponent(chatModelOverride.trim())}` : '';
      const result = await api.post<{
        success: boolean;
        data?: {
          connected: boolean;
          missingConfigKeys?: string[];
          missingCredentialKeys?: string[];
          message?: string;
          modelCount?: number;
          models?: ModelRouterModelInfo[];
        };
      }>(`/admin/providers/credentials/${id}/test${query}`);
      const connected = result.data?.connected === true;
      setTestFeedback((prev) => ({
        ...prev,
        [id]: {
          ok: connected,
          message:
            result.data?.message ||
            (connected
              ? 'Verbindung OK'
              : `Fehlende Felder: config(${(result.data?.missingConfigKeys ?? []).join(', ') || '-'}) credentials(${(result.data?.missingCredentialKeys ?? []).join(', ') || '-'})`),
        },
      }));
      const cred = credentials.find((c) => c.id === id);
      if (cred?.type === 'modelrouter' && result.data?.models?.length) {
        setRouterModels(result.data.models);
      }
      const updated = await api.get<{ data?: ProviderCredential[] }>('/admin/providers/credentials');
      setCredentials(updated.data ?? []);
    } catch {
      setTestFeedback((prev) => ({
        ...prev,
        [id]: {
          ok: false,
          message: 'Test fehlgeschlagen',
        },
      }));
      setError('Credential-Test fehlgeschlagen.');
    } finally {
      setTestingCredentialId(null);
    }
  };

  const testCredentialDraft = async () => {
    setTestingCredentialDraft(true);
    setCredentialDraftTestFeedback(null);
    setError(null);
    try {
      if (editingCredentialId) {
        const credsRaw = newCredential.credentials.trim();
        const credsEmpty = !credsRaw || credsRaw === '{}';
        if (credsEmpty) {
          const result = await api.post<{
            success: boolean;
            data?: { connected: boolean; message?: string; missingConfigKeys?: string[]; missingCredentialKeys?: string[] };
          }>(`/admin/providers/credentials/${editingCredentialId}/test`);
          const connected = result.data?.connected === true;
          setCredentialDraftTestFeedback({
            ok: connected,
            message: result.data?.message || (connected ? 'Verbindung OK' : 'Verbindung fehlgeschlagen'),
          });
          return;
        }
      }

      const parsedConfig = JSON.parse(newCredential.config || '{}');
      const parsedCredentials = JSON.parse(newCredential.credentials || '{}');
      const result = await api.post<{
        success: boolean;
        data?: { connected: boolean; message?: string; missingConfigKeys?: string[]; missingCredentialKeys?: string[] };
      }>('/admin/providers/credentials/test-draft', {
        type: newCredential.type,
        config: parsedConfig,
        credentials: parsedCredentials,
      });
      const connected = result.data?.connected === true;
      setCredentialDraftTestFeedback({
        ok: connected,
        message:
          result.data?.message ||
          (connected
            ? 'Verbindung OK'
            : `Fehlende Felder: config(${(result.data?.missingConfigKeys ?? []).join(', ') || '-'}) credentials(${(result.data?.missingCredentialKeys ?? []).join(', ') || '-'})`),
      });
      if (newCredential.type === 'modelrouter') {
        const models = (result.data as { models?: ModelRouterModelInfo[] } | undefined)?.models;
        if (Array.isArray(models)) setDraftRouterModels(models);
      }
    } catch (e) {
      setCredentialDraftTestFeedback({
        ok: false,
        message: (e as Error).message || 'Test fehlgeschlagen',
      });
    } finally {
      setTestingCredentialDraft(false);
    }
  };

  const testAiConfiguration = async () => {
    const credId = settings.ai_default_credential_id?.trim();
    if (!credId) {
      setAiConfigTestFeedback({
        ok: false,
        message:
          'Bitte zuerst ein Provider-Profil auswählen. Lege unter «Provider Credentials» ein Profil an (z. B. Model Router), teste es dort, und wähle es hier aus.',
      });
      return;
    }
    setTestingAiConfig(true);
    setAiConfigTestFeedback(null);
    setError(null);
    try {
      const modelOverride = settings.ai_default_model?.trim();
      const result = await api.post<{
        success: boolean;
        data?: { connected: boolean; message?: string; credential_name?: string; provider_type?: string; models?: ModelRouterModelInfo[] };
        error?: string;
      }>('/admin/ai/test-config', {
        credential_id: credId,
        ...(modelOverride ? { chat_model_override: modelOverride } : {}),
      });
      if (!result.success) {
        throw new Error(result.error || 'Test fehlgeschlagen');
      }
      const connected = result.data?.connected === true;
      if (result.data?.models?.length) setRouterModels(result.data.models);
      setAiConfigTestFeedback({
        ok: connected,
        message:
          result.data?.message ||
          (connected
            ? `Verbindung OK (${result.data?.credential_name ?? 'Profil'})`
            : 'Verbindung fehlgeschlagen'),
      });
    } catch (e) {
      setAiConfigTestFeedback({
        ok: false,
        message: (e as Error).message || 'Verbindungstest fehlgeschlagen',
      });
    } finally {
      setTestingAiConfig(false);
    }
  };

  const startEditCredential = (credential: ProviderCredential) => {
    setEditingCredentialId(credential.id);
    setNewCredential({
      type: credential.type,
      name: credential.name,
      config: JSON.stringify(credential.config || {}, null, 2),
      credentials: '{}',
    });
    setCredentialModalOpen(true);
  };

  const closeCredentialModal = () => {
    setCredentialModalOpen(false);
    setEditingCredentialId(null);
    setNewCredential({ type: 'openai', name: '', config: '{}', credentials: '{}' });
    setCredentialDraftTestFeedback(null);
  };

  const deleteCredential = async (id: string) => {
    setDeletingCredentialId(id);
    setError(null);
    try {
      await api.delete(`/admin/providers/credentials/${id}`);
      setCredentials((prev) => prev.filter((c) => c.id !== id));
      setTestFeedback((prev) => { const next = { ...prev }; delete next[id]; return next; });
    } catch (e) {
      setError((e as Error).message ?? 'Credential konnte nicht gelöscht werden.');
    } finally {
      setDeletingCredentialId(null);
      setConfirmDeleteId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-border border-t-transparent" />
      </div>
    );
  }

  if (!settingsLoaded) {
    return (
      <Card>
        <CardHeader><CardTitle>Einstellungen</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p role="alert" className="text-sm text-danger">{error || 'Einstellungen konnten nicht geladen werden.'}</p>
          <Button variant="outline" onClick={() => setSettingsAttempt((attempt) => attempt + 1)}>Erneut laden</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex h-full flex-col gap-0">
      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">Einstellungen</h1>
      </div>

      {/* Tab bar */}
      <div className="border-b border-border">
        <nav className="-mb-px flex gap-0 overflow-x-auto" aria-label="Einstellungen">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => { setActiveTab(tab.id); setError(null); }}
              className={cn(
                'flex shrink-0 items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors focus:outline-none',
                activeTab === tab.id
                  ? 'border-border text-foreground dark:text-muted'
                  : 'border-transparent text-muted hover:border-border hover:text-foreground dark:hover:text-foreground'
              )}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Error banner */}
      {error && (
        <div className="mt-4 rounded-lg border border-warning/30 bg-warning-soft px-4 py-3 text-sm text-warning">
          {error}
        </div>
      )}

      {/* Tab content */}
      <div className="mt-6 flex-1">

        {activeTab === 'bugs' && <BugReportTargetPanel />}

        {/* ── Allgemein ─────────────────────────────────────── */}
        {activeTab === 'general' && (
          <Card className="border-border bg-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Allgemein
              </CardTitle>
              <p className="text-sm text-muted">App-Name, Logo und Registrierung.</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-2">
                <Label>App-Name</Label>
                <Input value={settings.app_name} onChange={(e) => update('app_name', e.target.value)} placeholder="Leftclick AG" />
              </div>
              <div className="grid gap-2">
                <Label>Logo (helle Darstellung)</Label>
                <p className="text-sm text-muted">
                  Navigation im hellen Modus und PDF-Export. Hochladen (PNG, JPEG, WebP, SVG, max. 2 MB) oder URL.
                </p>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch">
                  <div className="flex h-24 w-full min-w-0 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-background dark:bg-card-hover sm:h-auto sm:w-28">
                    {settings.app_logo_url ? (
                      <img
                        src={settings.app_logo_url}
                        alt=""
                        className="max-h-full max-w-full object-contain p-2"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <span className="px-2 text-center text-xs text-muted">Vorschau</span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1 space-y-2">
                    <Input
                      value={settings.app_logo_url}
                      onChange={(e) => update('app_logo_url', e.target.value)}
                      placeholder="https://… oder /assets/…"
                      className="w-full"
                    />
                    <input
                      ref={logoLightInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                      className="hidden"
                      tabIndex={-1}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        void uploadLogoFile(f, 'light');
                        e.target.value = '';
                      }}
                    />
                    <button
                      type="button"
                      disabled={logoUploading !== null}
                      onClick={() => logoLightInputRef.current?.click()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          logoLightInputRef.current?.click();
                        }
                      }}
                      className={cn(
                        'flex w-full min-h-[44px] cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed px-3 py-3 text-center text-sm transition-colors sm:min-h-0 sm:flex-row sm:gap-2',
                        logoDragVariant === 'light'
                          ? 'border-border bg-card-hover dark:bg-primary-hover/30'
                          : 'border-border hover:border-border',
                        logoUploading !== null && 'pointer-events-none opacity-60'
                      )}
                      onDragEnter={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setLogoDragVariant('light');
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setLogoDragVariant('light');
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setLogoDragVariant((v) => (v === 'light' ? null : v));
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setLogoDragVariant(null);
                        const f = e.dataTransfer.files?.[0];
                        void uploadLogoFile(f, 'light');
                      }}
                    >
                      {logoUploading === 'light' ? (
                        <Loader2 className="h-4 w-4 shrink-0 animate-spin text-foreground" />
                      ) : (
                        <Upload className="h-4 w-4 shrink-0 text-muted" />
                      )}
                      <span className="text-muted">
                        {logoUploading === 'light' ? 'Wird hochgeladen…' : 'Datei ablegen oder klicken'}
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              <div className="grid gap-2">
                <Label>Logo (dunkle Darstellung / Negativ)</Label>
                <p className="text-sm text-muted">
                  Navigation und Login bei dunklem Modus. Wenn leer, wird das helle Logo verwendet.
                </p>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch">
                  <div className="flex h-24 w-full min-w-0 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-primary sm:h-auto sm:w-28">
                    {settings.app_logo_url_dark ? (
                      <img
                        src={settings.app_logo_url_dark}
                        alt=""
                        className="max-h-full max-w-full object-contain p-2"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <span className="px-2 text-center text-xs text-muted">Vorschau</span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1 space-y-2">
                    <Input
                      value={settings.app_logo_url_dark}
                      onChange={(e) => update('app_logo_url_dark', e.target.value)}
                      placeholder="https://… oder /assets/…"
                      className="w-full"
                    />
                    <input
                      ref={logoDarkInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                      className="hidden"
                      tabIndex={-1}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        void uploadLogoFile(f, 'dark');
                        e.target.value = '';
                      }}
                    />
                    <button
                      type="button"
                      disabled={logoUploading !== null}
                      onClick={() => logoDarkInputRef.current?.click()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          logoDarkInputRef.current?.click();
                        }
                      }}
                      className={cn(
                        'flex w-full min-h-[44px] cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed px-3 py-3 text-center text-sm transition-colors sm:min-h-0 sm:flex-row sm:gap-2',
                        logoDragVariant === 'dark'
                          ? 'border-border bg-card-hover dark:bg-primary-hover/30'
                          : 'border-border hover:border-border',
                        logoUploading !== null && 'pointer-events-none opacity-60'
                      )}
                      onDragEnter={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setLogoDragVariant('dark');
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setLogoDragVariant('dark');
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setLogoDragVariant((v) => (v === 'dark' ? null : v));
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setLogoDragVariant(null);
                        const f = e.dataTransfer.files?.[0];
                        void uploadLogoFile(f, 'dark');
                      }}
                    >
                      {logoUploading === 'dark' ? (
                        <Loader2 className="h-4 w-4 shrink-0 animate-spin text-foreground" />
                      ) : (
                        <Upload className="h-4 w-4 shrink-0 text-muted" />
                      )}
                      <span className="text-muted">
                        {logoUploading === 'dark' ? 'Wird hochgeladen…' : 'Datei ablegen oder klicken'}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-border p-4">
                <div>
                  <Label>Registrierung erlauben</Label>
                  <p className="text-sm text-muted">Neue Benutzer können sich selbst registrieren.</p>
                </div>
                <Switch checked={settings.registration_enabled} onCheckedChange={(v) => update('registration_enabled', v)} />
              </div>
              <div className="flex items-center justify-between rounded-lg border border-border p-4">
                <div>
                  <Label>Einladung erforderlich</Label>
                  <p className="text-sm text-muted">Nur mit gültigem Einladungscode registrierbar.</p>
                </div>
                <Switch checked={settings.invite_required} onCheckedChange={(v) => update('invite_required', v)} />
              </div>
              <div className="grid gap-2">
                <Label>Standard-Rolle</Label>
                <Select value={settings.default_role} onValueChange={(v) => update('default_role', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {roles.map((r: any) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={() => saveSection('general')} disabled={saving === 'general'}>
                {saving === 'general' ? (
                  <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Speichern…</span>
                ) : (
                  <><Save className="h-4 w-4" />Allgemein speichern</>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* ── PDF Export ────────────────────────────────────── */}
        {activeTab === 'pdf' && (
          <Card className="border-border bg-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                PDF Export
              </CardTitle>
              <p className="text-sm text-muted">
                Standardwerte fuer PDF-Reports zentral steuern.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-2">
                <Label>Aktivitaets-Report Titel</Label>
                <Input
                  value={settings.pdf_activity_report_title || ''}
                  onChange={(e) => update('pdf_activity_report_title', e.target.value)}
                  placeholder="Marketing-Aktivitaet Detailreport"
                />
              </div>
              <div className="grid gap-2">
                <Label>Ziel-Fallback Text (optional)</Label>
                <Input
                  value={settings.pdf_goal_fallback_text || ''}
                  onChange={(e) => update('pdf_goal_fallback_text', e.target.value)}
                  placeholder="Zieltext, falls in der Aktivitaet keine Zielsetzung gepflegt ist."
                />
              </div>
              <div className="flex items-center justify-between rounded-lg border border-border p-4">
                <div>
                  <Label>Logo im Header anzeigen</Label>
                  <p className="text-sm text-muted">Nutzt immer das helle Logo aus Allgemein (nicht die dunkle Variante).</p>
                </div>
                <Switch checked={!!settings.pdf_show_logo} onCheckedChange={(v) => update('pdf_show_logo', v)} />
              </div>
              <div className="flex items-center justify-between rounded-lg border border-border p-4">
                <div>
                  <Label>Checkliste standardmaessig einbeziehen</Label>
                  <p className="text-sm text-muted">Gilt als Default beim Aktivitaets-PDF Export.</p>
                </div>
                <Switch
                  checked={!!settings.pdf_include_checklists_default}
                  onCheckedChange={(v) => update('pdf_include_checklists_default', v)}
                />
              </div>
              <div className="flex items-center justify-between rounded-lg border border-border p-4">
                <div>
                  <Label>Kommentare standardmaessig einbeziehen</Label>
                  <p className="text-sm text-muted">Gilt als Default beim Aktivitaets-PDF Export.</p>
                </div>
                <Switch
                  checked={!!settings.pdf_include_comments_default}
                  onCheckedChange={(v) => update('pdf_include_comments_default', v)}
                />
              </div>
              <Button onClick={() => saveSection('pdf')} disabled={saving === 'pdf'}>
                {saving === 'pdf' ? (
                  <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Speichern…</span>
                ) : (
                  <><Save className="h-4 w-4" />PDF Einstellungen speichern</>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* ── Login Ambience ────────────────────────────────── */}
        {activeTab === 'ambience' && (
          <Card className="border-border bg-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5" />
                Login Ambience
              </CardTitle>
              <p className="text-sm text-muted">
                Texte und visuelle Paletten fuer den Login-Hintergrund zentral aus dem Backend steuern.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between rounded-lg border border-border p-4">
                <div>
                  <Label>Ambience aktiv</Label>
                  <p className="text-sm text-muted">Wenn deaktiviert, nutzt das System sichere Defaults.</p>
                </div>
                <Switch
                  checked={ambienceConfig.is_enabled}
                  onCheckedChange={(v) => setAmbienceConfig((prev) => ({ ...prev, is_enabled: v }))}
                />
              </div>
              <div className="grid gap-2">
                <Label>Headlines (eine pro Zeile)</Label>
                <Textarea
                  value={ambienceDraft.headlines}
                  onChange={(e) => setAmbienceDraft((prev) => ({ ...prev, headlines: e.target.value }))}
                  rows={4}
                  className={cn('w-full rounded-md border border-border bg-card p-2 text-sm')}
                />
              </div>
              <div className="grid gap-2">
                <Label>Sublines (eine pro Zeile)</Label>
                <Textarea
                  value={ambienceDraft.sublines}
                  onChange={(e) => setAmbienceDraft((prev) => ({ ...prev, sublines: e.target.value }))}
                  rows={4}
                  className={cn('w-full rounded-md border border-border bg-card p-2 text-sm')}
                />
              </div>
              <div className="grid gap-2">
                <Label>Highlights (eine pro Zeile)</Label>
                <Textarea
                  value={ambienceDraft.highlights}
                  onChange={(e) => setAmbienceDraft((prev) => ({ ...prev, highlights: e.target.value }))}
                  rows={5}
                  className={cn('w-full rounded-md border border-border bg-card p-2 text-sm')}
                />
              </div>
              <div className="grid gap-2">
                <Label>Paletten-JSON</Label>
                <Textarea
                  value={ambienceDraft.palettes}
                  onChange={(e) => setAmbienceDraft((prev) => ({ ...prev, palettes: e.target.value }))}
                  rows={9}
                  className={cn('w-full rounded-md border border-border bg-card p-2 font-mono text-xs')}
                />
                <p className="text-xs text-muted">
                  Format: Array mit Objekten wie {'{'} "bg1": "rgba(...)", "bg2": "...", "bg3": "..." {'}'}.
                </p>
              </div>
              <Button onClick={saveAmbienceConfig} disabled={saving === 'ambience'}>
                {saving === 'ambience' ? (
                  <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Speichern…</span>
                ) : (
                  <><Save className="h-4 w-4" />Login Ambience speichern</>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* ── Authentifizierung ─────────────────────────────── */}
        {activeTab === 'auth' && (
          <Card className="border-border bg-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Authentifizierung
              </CardTitle>
              <p className="text-sm text-muted">MFA und Sitzungsdauer.</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between rounded-lg border border-border p-4">
                <div>
                  <Label>MFA erzwingen</Label>
                  <p className="text-sm text-muted">Alle Benutzer müssen Zwei-Faktor-Authentifizierung aktivieren.</p>
                </div>
                <Switch checked={settings.mfa_enforced} onCheckedChange={(v) => update('mfa_enforced', v)} />
              </div>
              <div className="grid gap-2">
                <Label>Sitzungs-Timeout (Minuten)</Label>
                <Input type="number" min={5} max={1440} value={settings.session_timeout_minutes} onChange={(e) => update('session_timeout_minutes', Number(e.target.value) || 60)} />
              </div>
              <Button onClick={() => saveSection('auth')} disabled={saving === 'auth'}>
                {saving === 'auth' ? (
                  <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Speichern…</span>
                ) : (
                  <><Save className="h-4 w-4" />Auth speichern</>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* ── Benachrichtigungen ────────────────────────────── */}
        {activeTab === 'notifications' && (
          <Card className="border-border bg-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Benachrichtigungen
              </CardTitle>
              <p className="text-sm text-muted">E-Mail-Benachrichtigungen.</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between rounded-lg border border-border p-4">
                <div>
                  <Label>E-Mail-Benachrichtigungen aktiv</Label>
                  <p className="text-sm text-muted">System kann E-Mails versenden.</p>
                </div>
                <Switch checked={settings.notification_email_enabled} onCheckedChange={(v) => update('notification_email_enabled', v)} />
              </div>
              <div className="grid gap-2">
                <Label>Absender-Adresse</Label>
                <Input type="email" value={settings.notification_email_from} onChange={(e) => update('notification_email_from', e.target.value)} placeholder="noreply@beispiel.ch" />
              </div>
              <Button onClick={() => saveSection('notification')} disabled={saving === 'notification'}>
                {saving === 'notification' ? (
                  <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Speichern…</span>
                ) : (
                  <><Save className="h-4 w-4" />Benachrichtigungen speichern</>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* ── Social Hub ────────────────────────────────────── */}
        {activeTab === 'social' && (() => {
          const quiet = parseSocialQuietHours(settings.social_quiet_hours);
          const utm = parseSocialUtmDefaults(settings.social_utm_defaults);
          return (
            <Card className="w-full border-border bg-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Share2 className="h-5 w-5" />
                  Social Hub
                </CardTitle>
                <p className="text-sm text-muted">
                  Zeitzone, Ruhezeiten, Freigaben, Stories, UTM und Benachrichtigungen für Social Publishing.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-2">
                  <LabelWithHint
                    htmlFor="social_default_timezone"
                    label="Standard-Zeitzone"
                    hint="IANA-Zeitzone für Planung und Anzeige im Social Hub und Kalender (z. B. Europe/Zurich)."
                  />
                  <Input
                    id="social_default_timezone"
                    value={settings.social_default_timezone || 'Europe/Zurich'}
                    onChange={(e) => update('social_default_timezone', e.target.value)}
                    placeholder="Europe/Zurich"
                  />
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="grid gap-2">
                    <LabelWithHint
                      htmlFor="social_quiet_from"
                      label="Ruhezeit von"
                      hint="In dieser Zeit werden geplante Posts nicht automatisch veröffentlicht (Beginn der Quiet Hours)."
                    />
                    <Input
                      id="social_quiet_from"
                      type="time"
                      value={quiet.from}
                      onChange={(e) =>
                        update(
                          'social_quiet_hours',
                          JSON.stringify({ from: e.target.value || '22:00', to: quiet.to })
                        )
                      }
                    />
                  </div>
                  <div className="grid gap-2">
                    <LabelWithHint
                      htmlFor="social_quiet_to"
                      label="Ruhezeit bis"
                      hint="Ende der Quiet Hours; danach dürfen geplante Veröffentlichungen wieder laufen."
                    />
                    <Input
                      id="social_quiet_to"
                      type="time"
                      value={quiet.to}
                      onChange={(e) =>
                        update(
                          'social_quiet_hours',
                          JSON.stringify({ from: quiet.from, to: e.target.value || '07:00' })
                        )
                      }
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between gap-4 rounded-lg border border-border p-4">
                  <div className="min-w-0">
                    <LabelWithHint
                      label="Freigabe erforderlich"
                      hint="Wenn aktiv, müssen Social-Posts vor dem Publish durch einen Approver freigegeben werden."
                    />
                  </div>
                  <Switch
                    checked={!!settings.social_require_approval}
                    onCheckedChange={(v) => update('social_require_approval', v)}
                  />
                </div>

                <div className="grid gap-2">
                  <LabelWithHint
                    htmlFor="social_story_max_hours_ahead"
                    label="Stories: max. Stunden im Voraus"
                    hint="Maximale Vorlaufzeit für Story-Planung (Meta Stories laufen zeitlich begrenzt)."
                  />
                  <Input
                    id="social_story_max_hours_ahead"
                    type="number"
                    min={1}
                    max={168}
                    value={settings.social_story_max_hours_ahead ?? 24}
                    onChange={(e) => update('social_story_max_hours_ahead', Number(e.target.value) || 24)}
                  />
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="grid gap-2">
                    <LabelWithHint
                      htmlFor="social_utm_source"
                      label="UTM Source (Default)"
                      hint="Standard-utm_source für Links im Composer, wenn Link-Tracking aktiv ist."
                    />
                    <Input
                      id="social_utm_source"
                      value={utm.utm_source}
                      onChange={(e) =>
                        update(
                          'social_utm_defaults',
                          JSON.stringify({ utm_source: e.target.value, utm_medium: utm.utm_medium })
                        )
                      }
                      placeholder="leftclick"
                    />
                  </div>
                  <div className="grid gap-2">
                    <LabelWithHint
                      htmlFor="social_utm_medium"
                      label="UTM Medium (Default)"
                      hint="Standard-utm_medium für Social-Links (z. B. social)."
                    />
                    <Input
                      id="social_utm_medium"
                      value={utm.utm_medium}
                      onChange={(e) =>
                        update(
                          'social_utm_defaults',
                          JSON.stringify({ utm_source: utm.utm_source, utm_medium: e.target.value })
                        )
                      }
                      placeholder="social"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between gap-4 rounded-lg border border-border p-4">
                  <div className="min-w-0">
                    <LabelWithHint
                      label="Link-Tracking standardmässig"
                      hint="Neue Posts erhalten automatisch UTM-Parameter an Links, sofern nicht abweichend gesetzt."
                    />
                  </div>
                  <Switch
                    checked={!!settings.social_default_link_tracking}
                    onCheckedChange={(v) => update('social_default_link_tracking', v)}
                  />
                </div>

                <div className="flex items-center justify-between gap-4 rounded-lg border border-border p-4">
                  <div className="min-w-0">
                    <LabelWithHint
                      label="Bei Publish-Fehler benachrichtigen"
                      hint="Sendet System-Benachrichtigungen (In-App/E-Mail gemäss Profil), wenn ein Publish fehlschlägt."
                    />
                  </div>
                  <Switch
                    checked={!!settings.social_notify_on_fail}
                    onCheckedChange={(v) => update('social_notify_on_fail', v)}
                  />
                </div>

                <Button onClick={() => saveSection('social')} disabled={saving === 'social'}>
                  {saving === 'social' ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Speichern…
                    </span>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      Social-Einstellungen speichern
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          );
        })()}

        {/* ── KI / AI ──────────────────────────────────────── */}
        {activeTab === 'ai' && (
          <Card className="border-border bg-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Cpu className="h-5 w-5" />
                KI / AI
              </CardTitle>
              <p className="text-sm text-muted">
                Chat &amp; Modelle, Assistant-Verhalten und Deep Research – jeweils in eigenen Bereichen.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <AiSettingsSection
                icon={BookOpen}
                title="Einrichtung &amp; Anbindung"
                description="Schritte, bevor du unten speicherst: Credentials anlegen, Profil wählen, testen."
              >
                <div className="space-y-2">
                  {AI_CONNECT_GUIDE.map((item) => (
                    <div key={item.title} className="rounded-md border border-border bg-card px-3 py-2">
                      <p className="text-xs font-medium text-foreground">{item.title}</p>
                      <p className="text-xs text-muted">{item.text}</p>
                    </div>
                  ))}
                </div>
              </AiSettingsSection>

              <AiSettingsSection
                icon={MessageSquare}
                title="Chat &amp; Standard-LLM"
                description="Fallback-Provider, welches Credential der Chat nutzt und welches Modell gilt."
              >
                <div className="grid gap-2">
                  <Label>Standard-Provider</Label>
                  <Select value={settings.ai_default_provider} onValueChange={(v) => update('ai_default_provider', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="openai">OpenAI</SelectItem>
                      <SelectItem value="azure">Azure OpenAI</SelectItem>
                      <SelectItem value="modelrouter">Model Router</SelectItem>
                      <SelectItem value="ollama">Ollama</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted">Fallback-Provider, falls kein fixes Provider-Profil ausgewählt ist.</p>
                </div>
                <div className="grid gap-2">
                  <Label>Provider-Profil (aus Credentials)</Label>
                  <Select
                    value={settings.ai_default_credential_id || 'none'}
                    onValueChange={(v) => {
                      if (v === 'none') {
                        update('ai_default_credential_id', '');
                        return;
                      }
                      const selectedCredential = aiProviderCredentials.find((item) => item.id === v);
                      update('ai_default_credential_id', v);
                      if (selectedCredential) {
                        update('ai_default_provider', mapCredentialTypeToProvider(selectedCredential.type));
                        const configuredModel =
                          typeof selectedCredential.config?.model === 'string'
                            ? selectedCredential.config.model
                            : typeof selectedCredential.config?.deployment === 'string'
                              ? selectedCredential.config.deployment
                              : null;
                        if (configuredModel) {
                          update('ai_default_model', configuredModel);
                        }
                      }
                    }}
                  >
                    <SelectTrigger><SelectValue placeholder="Automatisch nach Standard-Provider" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Kein fixes Profil</SelectItem>
                      {aiProviderCredentials.map((credential) => (
                        <SelectItem key={credential.id} value={credential.id}>
                          {credential.name} ({providerLabel(credential.type)})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted">Wenn gewählt, nutzt der Chat genau dieses gespeicherte Credential-Profil.</p>
                </div>
                <div className="grid gap-2">
                  {isModelRouterAiActive ? (
                    <ModelRouterModelSelect
                      label="Standard-Modell (Model Router)"
                      value={settings.ai_default_model || 'auto'}
                      onChange={(v) => update('ai_default_model', v)}
                      models={filterRouterModels(routerModels, 'chat')}
                      loading={loadingRouterModels}
                      onRefresh={loadRouterModelsForSettings}
                      allowAuto
                      placeholder="auto"
                      hint={`Gateway: ${MODELROUTER_DEFAULT_BASE_URL}. Namen kommen live von GET /v1/models (z. B. GLM-4.7-Flash, GLM-5).`}
                    />
                  ) : (
                    <>
                      <Label>Standard-Modell</Label>
                      <Input
                        value={settings.ai_default_model}
                        onChange={(e) => update('ai_default_model', e.target.value)}
                        placeholder="gpt-4o"
                      />
                      <p className="text-xs text-muted">Wird verwendet, wenn im gewählten Profil kein Modell gesetzt ist.</p>
                    </>
                  )}
                </div>
                <div className="rounded-lg border border-border bg-card p-4 dark:bg-card/50">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground">Verbindung testen</p>
                      <p className="text-xs text-muted">
                        Prüft das gewählte Provider-Profil (Gateway, Key, optional Modell) — ohne Speichern der KI-Einstellungen.
                      </p>
                      {aiConfigTestFeedback && (
                        <p
                          className={cn(
                            'mt-2 text-xs',
                            aiConfigTestFeedback.ok ? 'text-success' : 'text-danger'
                          )}
                        >
                          {aiConfigTestFeedback.message}
                        </p>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full shrink-0 sm:w-auto"
                      disabled={testingAiConfig}
                      onClick={testAiConfiguration}
                    >
                      {testingAiConfig ? (
                        <><Loader2 className="mr-1 h-4 w-4 animate-spin" />Teste…</>
                      ) : (
                        <><FlaskConical className="mr-1 h-4 w-4" />Verbindung testen</>
                      )}
                    </Button>
                  </div>
                </div>
              </AiSettingsSection>

              <AiSettingsSection
                icon={Mic}
                title="Spracherkennung (Voice)"
                description="Server-Transkription für Browser ohne Web Speech API (z. B. Firefox): OpenAI (whisper-1) oder Azure (eigenes Transcription-Deployment, z. B. gpt-4o-mini-transcribe)."
              >
                <div className="w-full space-y-3 rounded-lg border border-border bg-card p-4 dark:bg-card/50">
                  <p className="text-sm text-muted">
                    <strong className="font-medium text-foreground">Model Router:</strong> Credential-Typ „Model Router“ mit Gateway-URL (
                    <code className="rounded bg-card-hover px-1 font-mono text-xs">baseURL</code> inkl.{' '}
                    <code className="rounded bg-card-hover px-1 font-mono text-xs">/v1</code>) und{' '}
                    <code className="rounded bg-card-hover px-1 font-mono text-xs">mr_*</code>-Key. Optional{' '}
                    <code className="rounded bg-card-hover px-1 font-mono text-xs">transcription_model</code> für Voice.{' '}
                    <strong className="font-medium text-foreground">OpenAI:</strong> Credential-Typ „OpenAI“ mit API-Key (oder{' '}
                    <code className="rounded bg-card-hover px-1 font-mono text-xs">OPENAI_API_KEY</code> auf dem Server).{' '}
                    <strong className="font-medium text-foreground">Azure OpenAI:</strong> Typ „Azure OpenAI“ mit Endpoint und Key; im Config JSON zusätzlich{' '}
                    <code className="rounded bg-card-hover px-1 font-mono text-xs">whisper_deployment</code> = Name des Audio-Transcription-Deployments in Azure (laut
                    Portal; z. B. <code className="rounded bg-card-hover px-1 font-mono text-xs">gpt-4o-mini-transcribe</code> — oft günstiger als{' '}
                    <code className="rounded bg-card-hover px-1 font-mono text-xs">gpt-4o-transcribe</code> oder OpenAI whisper-1; nicht dasselbe wie das Chat-Deployment). Optional{' '}
                    <code className="rounded bg-card-hover px-1 font-mono text-xs">whisper_api_version</code>{' '}
                    (Standard <code className="rounded bg-card-hover px-1 font-mono text-xs">2024-06-01</code>; für neuere Modelle oft{' '}
                    <code className="rounded bg-card-hover px-1 font-mono text-xs">2025-03-01-preview</code>). Alternativ reine Umgebungsvariablen{' '}
                    <code className="rounded bg-card-hover px-1 font-mono text-xs">AZURE_OPENAI_ENDPOINT</code>,{' '}
                    <code className="rounded bg-card-hover px-1 font-mono text-xs">AZURE_OPENAI_API_KEY</code>,{' '}
                    <code className="rounded bg-card-hover px-1 font-mono text-xs">AZURE_OPENAI_WHISPER_DEPLOYMENT</code>.
                  </p>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    {voiceServerLikelyConfigured ? (
                      <Badge variant="outline" className="w-fit border-success/30 text-success">
                        OpenAI, Azure-Transcription (whisper_deployment) oder Model Router hinterlegt
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="w-fit border-warning/30 text-warning">
                        Kein OpenAI-Credential und kein whisper_deployment (Azure) — Server-Voice eingeschränkt
                      </Badge>
                    )}
                    <Button type="button" variant="outline" size="sm" className="w-full shrink-0 sm:w-auto" onClick={() => setActiveTab('providers')}>
                      Zu Provider Credentials
                    </Button>
                  </div>
                </div>
              </AiSettingsSection>

              <AiSettingsSection
                icon={Bot}
                title="Assistant"
                description="Globale Schalter und Grenzen für den KI-Assistenten in der App."
              >
                <div className="flex items-center justify-between rounded-lg border border-border bg-card p-4 dark:bg-card/50">
                  <div>
                    <Label>Assistant aktivieren</Label>
                    <p className="text-sm text-muted">Globaler Feature-Flag für KI-Assistent.</p>
                  </div>
                  <Switch checked={!!settings.assistant_enabled} onCheckedChange={(v) => update('assistant_enabled', v)} />
                </div>
                <div className="grid gap-2">
                  <Label>Autonomie-Modus</Label>
                  <Select value={settings.assistant_autonomy_mode || 'mostly_auto_execute'} onValueChange={(v) => update('assistant_autonomy_mode', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Manual</SelectItem>
                      <SelectItem value="safe_auto">Safe Auto</SelectItem>
                      <SelectItem value="mostly_auto_execute">Mostly Auto Execute</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted">Manual = nur Antworten, Safe Auto = vorsichtige Aktionen, Mostly Auto = automatischer.</p>
                </div>
                <div className="grid gap-2">
                  <Label>Maximaler Risk-Score</Label>
                  <Input type="number" min={0} max={100} value={settings.assistant_max_risk_score ?? 70} onChange={(e) => update('assistant_max_risk_score', Number(e.target.value) || 70)} />
                  <p className="text-xs text-muted">0 = sehr streng, 100 = sehr tolerant. Empfehlung: 60-75 für Produktion.</p>
                </div>
              </AiSettingsSection>

              <AiSettingsSection
                icon={Search}
                title="Deep Research"
                description="Markt- und Wettbewerbsrecherche (Assistants, Strategie, Research-Runs)."
              >
                <div className="flex items-center justify-between rounded-lg border border-border bg-card p-4 dark:bg-card/50">
                  <div>
                    <Label>Deep Research aktiv</Label>
                    <p className="text-sm text-muted">Erweiterte Markt- und Wettbewerbsrecherche zulassen.</p>
                  </div>
                  <Switch checked={!!settings.assistant_deep_research_enabled} onCheckedChange={(v) => update('assistant_deep_research_enabled', v)} />
                </div>
                <div className="grid gap-2">
                  <Label>Standard-Forschungsfokus</Label>
                  <Select
                    value={settings.assistant_research_default_focus_area ?? 'market'}
                    onValueChange={(v) => update('assistant_research_default_focus_area', v as ResearchFocusArea)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="market">Marktanalyse</SelectItem>
                      <SelectItem value="competitors">Wettbewerb</SelectItem>
                      <SelectItem value="audience">Zielgruppe</SelectItem>
                      <SelectItem value="channels">Kanäle</SelectItem>
                      <SelectItem value="trends">Trends</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted">
                    Voreinstellung für «Fokus» beim Start von Deep Research (Strategie &amp; KI-Assistent). Sie können ihn pro Anfrage weiterhin ändern.
                  </p>
                </div>
                <div className="grid gap-2">
                  <Label>Deep Research Provider</Label>
                  <Select value={settings.assistant_research_default_provider || 'openai'} onValueChange={(v) => update('assistant_research_default_provider', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="openai">OpenAI (gpt-5.4-pro)</SelectItem>
                      <SelectItem value="modelrouter">Model Router (router.leftclick.ch)</SelectItem>
                      <SelectItem value="azure_cognitive_services">Azure Cognitive Services (gpt-5.4-pro)</SelectItem>
                      <SelectItem value="azure_foundry">Azure AI Foundry Legacy (o3-deep-research + Bing)</SelectItem>
                      <SelectItem value="custom_o3gpt">Custom O3GPT Endpoint</SelectItem>
                    </SelectContent>
                  </Select>
                  <ul className="list-inside list-disc space-y-1 text-xs text-muted">
                    <li><strong className="font-medium text-muted">Model Router:</strong> Credential unter Provider Credentials; Modell z. B. <code className="rounded bg-card-hover px-1 text-xs">auto</code> oder GLM-4.7-Flash.</li>
                    <li><strong className="font-medium text-muted">OpenAI / Azure Cognitive Services:</strong> passendes Credential unter Provider Credentials.</li>
                    <li><strong className="font-medium text-muted">Azure Foundry (Legacy):</strong> Bing Web Search – Projekt- und Bing-Connection in den App-Einstellungen / Credentials.</li>
                    <li><strong className="font-medium text-muted">Custom O3GPT:</strong> Endpoint unten eintragen.</li>
                  </ul>
                </div>
                <div className="grid gap-2">
                  <Label>Deep Research Modell</Label>
                  <Input value={settings.assistant_research_default_model || 'gpt-5.4-pro'} onChange={(e) => update('assistant_research_default_model', e.target.value)} placeholder="gpt-5.4-pro" />
                  <p className="text-xs text-muted">z. B. gpt-5.4-pro (OpenAI / Azure Cognitive Services) oder o3-deep-research (Azure Foundry Legacy).</p>
                </div>
                <div className="grid gap-2">
                  <Label>Custom O3GPT Endpoint</Label>
                  <Input value={settings.assistant_research_custom_endpoint || ''} onChange={(e) => update('assistant_research_custom_endpoint', e.target.value)} placeholder="https://..." />
                  <p className="text-xs text-muted">Nur ausfüllen, wenn Deep Research Provider auf Custom O3GPT steht.</p>
                </div>
              </AiSettingsSection>

              <div className="flex flex-col gap-2 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-muted">Änderungen gelten für Chat, Assistant und Deep Research nach dem Speichern.</p>
                <Button className="w-full sm:w-auto" onClick={() => saveSection('ai')} disabled={saving === 'ai'}>
                  {saving === 'ai' ? (
                    <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Speichern…</span>
                  ) : (
                    <><Save className="h-4 w-4" />KI-Einstellungen speichern</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── Provider Credentials ──────────────────────────── */}
        {activeTab === 'providers' && (
          <>
            <Card className="border-border bg-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <KeyRound className="h-5 w-5" />
                      Provider Credentials
                    </CardTitle>
                    <p className="mt-1 text-sm text-muted">
                      API-Keys und Provider-Konfigurationen verschlüsselt verwalten — ohne Server-.env. Für Model Router: Gateway-URL und{' '}
                      <code className="rounded bg-card-hover px-1 text-xs">mr_*</code>-Key hier hinterlegen und mit «Testen» prüfen.
                    </p>
                  </div>
                  <Button onClick={() => { setCredentialDraftTestFeedback(null); setCredentialModalOpen(true); }}>
                    <Plus className="h-4 w-4" />
                    Credential hinzufügen
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {credentialLoading && <p className="text-sm text-muted">Lade Credentials...</p>}
                {!credentialLoading && credentials.length === 0 && (
                  <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-12 text-center">
                    <KeyRound className="mb-3 h-8 w-8 text-muted" />
                    <p className="text-sm font-medium text-muted">Noch keine Credentials hinterlegt</p>
                    <p className="mt-1 text-xs text-muted">Klicke auf "Credential hinzufügen" um loszulegen.</p>
                  </div>
                )}
                {credentials.length > 0 && (
                  <div className="space-y-2">
                    {credentials.map((credential) => {
                      const feedback = testFeedback[credential.id];
                      return (
                        <div key={credential.id} className="rounded-lg border border-border p-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-foreground">{credential.name}</p>
                              <p className="text-xs text-muted">{providerLabel(credential.type)} · Status: {credential.status}</p>
                              <details className="mt-2 rounded border border-border p-2">
                                <summary className="cursor-pointer text-xs font-medium text-muted">Gespeicherte Werte anzeigen</summary>
                                <div className="mt-2 space-y-2">
                                  <div>
                                    <p className="text-[11px] uppercase text-muted">Config</p>
                                    <pre className="overflow-auto rounded bg-background p-2 text-[11px] dark:bg-card">{JSON.stringify(credential.config || {}, null, 2)}</pre>
                                  </div>
                                  <div>
                                    <p className="text-[11px] uppercase text-muted">Credentials (maskiert)</p>
                                    <pre className="overflow-auto rounded bg-background p-2 text-[11px] dark:bg-card">{JSON.stringify(credential.credentials || {}, null, 2)}</pre>
                                  </div>
                                </div>
                              </details>
                              {feedback && (
                                <p className={cn('mt-1 text-xs', feedback.ok ? 'text-success' : 'text-danger')}>
                                  {feedback.message}
                                </p>
                              )}
                            </div>
                            <div className="flex shrink-0 flex-col items-end gap-2">
                              <div className="flex gap-2">
                                <Button variant="outline" size="sm" onClick={() => startEditCredential(credential)}>
                                  Bearbeiten
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => testCredential(credential.id)}
                                  disabled={testingCredentialId === credential.id}
                                  className={cn(feedback?.ok && 'border-success/30 text-success')}
                                >
                                  {testingCredentialId === credential.id ? (
                                    <><Loader2 className="mr-1 h-4 w-4 animate-spin" />Teste...</>
                                  ) : (
                                    <><FlaskConical className="mr-1 h-4 w-4" />Testen</>
                                  )}
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setConfirmDeleteId(credential.id)}
                                  className="border-danger/30 text-danger hover:bg-danger-soft hover:text-danger-hover"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                              {confirmDeleteId === credential.id && (
                                <div className="flex items-center gap-2 rounded-lg border border-danger/30 bg-danger-soft px-3 py-2 text-xs">
                                  <span className="text-danger">Wirklich löschen?</span>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    className="h-6 px-2 text-xs"
                                    onClick={() => deleteCredential(credential.id)}
                                    disabled={deletingCredentialId === credential.id}
                                  >
                                    {deletingCredentialId === credential.id ? (
                                      <Loader2 className="h-3 w-3 animate-spin" />
                                    ) : (
                                      'Löschen'
                                    )}
                                  </Button>
                                  <button
                                    type="button"
                                    onClick={() => setConfirmDeleteId(null)}
                                    className="text-muted hover:text-foreground dark:hover:text-foreground"
                                  >
                                    <X className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Credential Add / Edit Modal */}
            <Dialog open={credentialModalOpen} onOpenChange={(open) => { if (!open) closeCredentialModal(); }}>
              <DialogContent className="max-h-[90vh] w-full max-w-xl overflow-y-auto">
                <DialogHeader className="pb-2">
                  <DialogTitle className="flex items-center gap-2 text-base">
                    <KeyRound className="h-4 w-4 text-muted" />
                    {editingCredentialId ? 'Credential bearbeiten' : 'Credential hinzufügen'}
                  </DialogTitle>
                </DialogHeader>

                <div className="space-y-5">
                  {editingCredentialId && (
                    <div className="rounded-lg border border-accent/30 bg-accent-soft px-4 py-3 text-sm text-accent">
                      Im Bearbeitungsmodus wird das Credentials-Feld nur überschrieben, wenn es ausgefüllt ist.
                    </div>
                  )}

                  {/* Typ + Name */}
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="grid gap-1.5">
                      <Label className="text-sm font-medium">Typ</Label>
                      <Select value={newCredential.type} onValueChange={(v) => setNewCredential((s) => ({ ...s, type: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="openai">OpenAI</SelectItem>
                          <SelectItem value="azure_openai">Azure OpenAI</SelectItem>
                          <SelectItem value="modelrouter">Model Router</SelectItem>
                          <SelectItem value="azure_cognitive_services">Azure Cognitive Services</SelectItem>
                          <SelectItem value="azure_foundry">Azure AI Foundry (Legacy)</SelectItem>
                          <SelectItem value="custom_o3gpt">Custom O3GPT</SelectItem>
                          <SelectItem value="google_ads">Google Ads</SelectItem>
                          <SelectItem value="linkedin_ads">LinkedIn Ads</SelectItem>
                          <SelectItem value="mailchimp">Mailchimp</SelectItem>
                          <SelectItem value="lemlist">Lemlist</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid gap-1.5">
                      <Label className="text-sm font-medium">Name</Label>
                      <Input
                        value={newCredential.name}
                        onChange={(e) => setNewCredential((s) => ({ ...s, name: e.target.value }))}
                        placeholder="z.B. Production OpenAI"
                      />
                    </div>
                  </div>

                  {/* Beispiel laden */}
                  <div className="flex items-center justify-between rounded-lg border border-border bg-background px-4 py-3 dark:bg-card-hover/40">
                    <div>
                      <p className="text-sm font-medium text-muted dark:text-foreground">Vorlage laden</p>
                      <p className="text-xs text-muted">Felder mit Beispielwerten befüllen, dann echte Keys ersetzen.</p>
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={applyProviderExample}>
                      Beispiel laden
                    </Button>
                  </div>

                  {/* Config JSON */}
                  <div className="grid gap-1.5">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">Config JSON</Label>
                      <span className="text-xs text-muted">Endpoint, Deployment, Modell etc.</span>
                    </div>
                    <Textarea
                      value={newCredential.config}
                      onChange={(e) => setNewCredential((s) => ({ ...s, config: e.target.value }))}
                      rows={5}
                      spellCheck={false}
                      className="w-full rounded-lg border border-border bg-card p-3 font-mono text-xs leading-relaxed focus:outline-none focus:ring-2 focus:ring-border"
                    />
                    {newCredential.type === 'modelrouter' && (
                      <div className="space-y-4 rounded-lg border border-accent/30 bg-accent-soft p-3">
                        <p className="text-xs text-accent">
                          <strong>Leftclick Model Router</strong> — OpenAI-kompatibles Gateway unter{' '}
                          <code>{MODELROUTER_DEFAULT_BASE_URL}</code>. API-Key (<code>mr_live_…</code>) aus der Router-Admin-UI.
                          Unterstützt Chat, Embeddings, Responses, Bilder, Audio (STT/TTS) und <code>auto</code>-Routing.
                        </p>
                        <ModelRouterModelSelect
                          label="Chat-Modell (Config: model)"
                          value={readDraftConfigField('model', 'auto')}
                          onChange={(v) => patchDraftConfigField('model', v)}
                          models={filterRouterModels(draftRouterModels, 'chat')}
                          loading={loadingDraftRouterModels}
                          onRefresh={loadRouterModelsForDraft}
                          allowAuto
                          placeholder="auto"
                        />
                        <ModelRouterModelSelect
                          label="Spracherkennung (Config: transcription_model)"
                          value={readDraftConfigField('transcription_model', '')}
                          onChange={(v) => patchDraftConfigField('transcription_model', v)}
                          models={filterRouterModels(draftRouterModels, 'transcription')}
                          loading={loadingDraftRouterModels}
                          onRefresh={loadRouterModelsForDraft}
                          allowAuto={false}
                          placeholder="Modell mit STT-Modality"
                          hint="Optional — leer lassen, wenn kein Voice über den Router."
                        />
                      </div>
                    )}
                    {newCredential.type === 'azure_openai' && (
                      <p className="text-xs text-warning">
                        Azure-Hinweis: Endpoint als Resource-Root eintragen, ohne <code>/models</code>.
                      </p>
                    )}
                    {newCredential.type === 'azure_cognitive_services' && (
                      <p className="text-xs text-warning">
                        <strong>Endpoint</strong>: Resource-Root aus Azure (Schlüssel und Endpunkt), z. B.{' '}
                        <code>https://leftclick-ai.cognitiveservices.azure.com</code> — oder dieselbe URL inkl.{' '}
                        <code>/openai/v1</code> (wird normalisiert). Die App ruft die Responses-API wie in der Microsoft-Doku unter{' '}
                        <code>…/openai/v1/responses</code> auf; das Deployment steht im JSON-Feld <code>deployment</code>{' '}
                        (Body <code>model</code>). Bei 401: Schlüssel und Host müssen zur <em>gleichen</em> Ressource passen.{' '}
                        <code>apiVersion</code> ist der Query-Parameter <strong>api-version</strong>. Für die Responses-API
                        empfehlen Microsoft-Beispiele oft den Literal <strong>preview</strong> (nicht nur datierte Versionen). Bei{' '}
                        <strong>400 API version not supported</strong>: zuerst <code>"preview"</code> setzen; optional Endpoint auf{' '}
                        <code>https://&lt;resource&gt;.openai.azure.com</code> (gleicher Schlüssel) — die App versucht Fallbacks
                        automatisch. (
                        <a
                          href="https://learn.microsoft.com/en-us/azure/ai-services/openai/quickstart"
                          className="underline"
                          target="_blank"
                          rel="noreferrer"
                        >
                          Microsoft Responses-Quickstart
                        </a>
                        ).
                      </p>
                    )}
                    <HintBlock
                      value={selectedProviderHint.config}
                      copied={copiedHint === 'config'}
                      onCopy={() => {
                        navigator.clipboard.writeText(selectedProviderHint.config);
                        setCopiedHint('config');
                        setTimeout(() => setCopiedHint(null), 2000);
                      }}
                      onUse={() => setNewCredential((s) => ({ ...s, config: selectedProviderHint.config }))}
                    />
                  </div>

                  {/* Credentials JSON */}
                  <div className="grid gap-1.5">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">Credentials JSON</Label>
                      <span className="text-xs text-muted">API-Keys, Tokens — werden verschlüsselt gespeichert.</span>
                    </div>
                    <Textarea
                      value={newCredential.credentials}
                      onChange={(e) => setNewCredential((s) => ({ ...s, credentials: e.target.value }))}
                      rows={5}
                      spellCheck={false}
                      className="w-full rounded-lg border border-border bg-card p-3 font-mono text-xs leading-relaxed focus:outline-none focus:ring-2 focus:ring-border"
                    />
                    <HintBlock
                      value={selectedProviderHint.credentials}
                      copied={copiedHint === 'credentials'}
                      onCopy={() => {
                        navigator.clipboard.writeText(selectedProviderHint.credentials);
                        setCopiedHint('credentials');
                        setTimeout(() => setCopiedHint(null), 2000);
                      }}
                      onUse={() => setNewCredential((s) => ({ ...s, credentials: selectedProviderHint.credentials }))}
                    />
                  </div>

                  {/* Actions */}
                  {credentialDraftTestFeedback && (
                    <p
                      className={cn(
                        'rounded-lg border px-3 py-2 text-xs',
                        credentialDraftTestFeedback.ok
                          ? 'border-success/30 bg-success-soft text-success'
                          : 'border-danger/30 bg-danger-soft text-danger'
                      )}
                    >
                      {credentialDraftTestFeedback.message}
                    </p>
                  )}
                  <div className="flex flex-wrap justify-end gap-2 border-t border-border pt-4">
                    <Button type="button" variant="outline" onClick={closeCredentialModal}>
                      Abbrechen
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={testCredentialDraft}
                      disabled={testingCredentialDraft || !newCredential.name.trim()}
                    >
                      {testingCredentialDraft ? (
                        <><Loader2 className="h-4 w-4 animate-spin" />Teste…</>
                      ) : (
                        <><FlaskConical className="h-4 w-4" />Verbindung testen</>
                      )}
                    </Button>
                    <Button onClick={saveCredential} disabled={saving === 'credentials' || !newCredential.name.trim()}>
                      {saving === 'credentials' ? (
                        <><Loader2 className="h-4 w-4 animate-spin" />Speichern…</>
                      ) : (
                        <><Save className="h-4 w-4" />{editingCredentialId ? 'Aktualisieren' : 'Speichern'}</>
                      )}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </>
        )}
      </div>
    </div>
  );
}
